import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { api } from '@/lib/api';
import { useFilters } from '@/lib/filters';
import { formatIsoDate, formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/*
 * Kalender — session tracking of the own-booking widget (booking_events): the
 * inside of the booking modal, the black box between «modal opened» and
 * «booked». Built like the Quiz view: KPI rates first, then the funnel, the
 * quiz-vs-LP split, and the session list with journey trails. Rows never carry
 * a name — the booking ref links a converted session to the /bookinger queue.
 */

const MESSAGES = {
  nb: {
    title: 'Kalender',
    sub: (from: string, to: string) =>
      `Booking-widgeten innvendig, perioden ${from} → ${to} — styres av datovelgeren øverst.`,
    statSessions: 'Åpninger',
    statSessionsSub: 'åpnet booking-vinduet',
    statSlotRate: 'Valgte time',
    statSlotRateSub: (n: string) => `${n} valgte et tidspunkt`,
    statDetailsRate: 'Skjema → booket',
    statDetailsRateSub: (n: string) => `${n} åpnet skjemaet`,
    statCompletionRate: 'Fullføringsrate',
    statCompletionRateSub: (n: string) => `${n} booket`,
    warnEmpty: (n: number) => `${n} økter fikk en TOM kalender — sjekk tilgjengeligheten`,
    warnCalErr: (n: number) => `${n} økter fikk ikke lastet kalenderen (nettverk/server)`,
    warnErrors: (parts: string) => `Innsendingsfeil: ${parts}`,
    funnelTitle: 'Trakten',
    funnelSub: 'Hvor mange når hvert steg — fra åpnet vindu til fullført booking.',
    stageOpened: 'Åpnet vinduet',
    stageSawCalendar: 'Så ledige timer',
    stageSelectedSlot: 'Valgte time',
    stageOpenedDetails: 'Åpnet skjemaet',
    stageAttempted: 'Sendte inn',
    stageBooked: 'Booket',
    sourceTitle: 'Quiz vs. landingsside',
    sourceSub: 'Samme trakt splittet på siden som åpnet vinduet.',
    colSource: 'Kilde',
    colSessions: 'Åpninger',
    colSlot: 'Valgte time',
    colAttempted: 'Sendte inn',
    colBooked: 'Booket',
    colRate: 'Fullføring',
    srcQuiz: 'Quiz',
    srcLp: 'Landingsside',
    srcOther: 'Annet',
    audienceTitle: 'Budskapsreise',
    audienceSub:
      'Økter med samtykket budskapsvisning, fordelt på budskap og tilbud. «Åpnet» er grensen inn i booking-trakten; dette er ikke kundens bekreftede alder.',
    colJourney: 'Budskap',
    colOffer: 'Tilbud',
    colMeasured: 'Samtykkede økter',
    colOpened: 'Åpnet booking',
    colPlan: 'Planvalg',
    journeyControl: 'Kontroll · 50+',
    journeyAntigym: 'Anti-gym · 25–49',
    journeyUnassigned: 'Ikke merket',
    offerBaseline: '749 / 899',
    offerUnassigned: 'Ikke merket',
    planTitle: 'Planvalg',
    planSub:
      'Siste registrerte planvalg blant økter med samtykket budskapsvisning. Økter uten valg står synlig som «Ikke valgt».',
    planAnnual749: '12 mnd · 749/mnd',
    planFlex899: 'Fleksibel · 899/mnd',
    planAnnualUpfront8990: '12 mnd · 8 990 på forhånd',
    planUnassigned: 'Ikke valgt',
    surfaceTitle: 'Tilbudsplassering',
    surfaceSub:
      'Samtykkede budskapsøkter per sted tilbudet ble vist. Én økt kan finnes i flere rader hvis den så flere plasseringer.',
    surfaceHero: 'Hero · prisslide',
    surfaceChoice: 'Valg mellom to planer',
    surfaceMembership: 'Medlemskapsplaner',
    surfaceUnassigned: 'Ikke merket',
    sessionsTitle: 'Siste økter',
    sessionsSub: 'Nyeste først — utvid en rad for hele reisen inne i vinduet.',
    colTime: 'Tidspunkt',
    colDevice: 'Enhet',
    colStatus: 'Status',
    expandRow: 'Utvid rad',
    adDotTitle: 'Annonse-klikk',
    quizLinkTitle: 'Kom fra quizen',
    emptyCalTitle: 'Fikk tom kalender',
    journeyTitle: 'Reisen',
    slotsShownN: (n: number) => `${n} timer`,
    refTitle: 'Booking-referanse',
    refHint: 'Finn personen i /bookinger med denne referansen.',
    deviceMobile: 'Mobil',
    deviceDesktop: 'Desktop',
    deviceUnknown: 'Ukjent',
    stages: {
      opened: 'Åpnet',
      saw_calendar: 'Så kalenderen',
      selected_slot: 'Valgte time',
      opened_details: 'Skjemaet',
      attempted: 'Sendte inn',
      booked: 'Booket',
    } as Record<string, string>,
    events: {
      journey_view: 'Så budskapsreisen',
      offer_view: 'Så tilbudet',
      plan_intent: 'Valgte plan',
      modal_open: 'Åpnet vinduet',
      flow_start: 'Startet flyten',
      calendar_view: 'Kalender vist',
      calendar_error: 'Kalender-feil',
      day_select: 'Valgte dag',
      slot_select: 'Valgte time',
      details_open: 'Åpnet skjemaet',
      submit_attempt: 'Sendte inn',
      submit_error: 'Innsendingsfeil',
      booked: 'Booket ✓',
      closed: 'Lukket vinduet',
      intake_open: 'Åpnet intake',
      intake_submit: 'Svarte på intake',
      intake_skip: 'Hoppet over intake',
    } as Record<string, string>,
    errorKinds: {
      validation: 'ugyldig skjema',
      slot_taken: 'timen tatt',
      server: 'serverfeil',
      network: 'nettverksfeil',
    } as Record<string, string>,
    emptyTitle: 'Ingen booking-økter i perioden',
    emptyHint:
      'Økter dukker opp her når kalender-beaconet er live på temaet og noen åpner booking-vinduet.',
  },
  en: {
    title: 'Calendar',
    sub: (from: string, to: string) =>
      `Inside the booking widget, period ${from} → ${to} — controlled by the date picker at the top.`,
    statSessions: 'Opens',
    statSessionsSub: 'opened the booking modal',
    statSlotRate: 'Picked a time',
    statSlotRateSub: (n: string) => `${n} selected a slot`,
    statDetailsRate: 'Form → booked',
    statDetailsRateSub: (n: string) => `${n} opened the form`,
    statCompletionRate: 'Completion rate',
    statCompletionRateSub: (n: string) => `${n} booked`,
    warnEmpty: (n: number) => `${n} sessions saw an EMPTY calendar — check availability`,
    warnCalErr: (n: number) => `${n} sessions failed to load the calendar (network/server)`,
    warnErrors: (parts: string) => `Submit errors: ${parts}`,
    funnelTitle: 'The funnel',
    funnelSub: 'How many reach each stage — from opened modal to completed booking.',
    stageOpened: 'Opened the modal',
    stageSawCalendar: 'Saw available times',
    stageSelectedSlot: 'Picked a time',
    stageOpenedDetails: 'Opened the form',
    stageAttempted: 'Submitted',
    stageBooked: 'Booked',
    sourceTitle: 'Quiz vs. landing page',
    sourceSub: 'The same funnel split by the page that opened the modal.',
    colSource: 'Source',
    colSessions: 'Opens',
    colSlot: 'Picked a time',
    colAttempted: 'Submitted',
    colBooked: 'Booked',
    colRate: 'Completion',
    srcQuiz: 'Quiz',
    srcLp: 'Landing page',
    srcOther: 'Other',
    audienceTitle: 'Message journey',
    audienceSub:
      "Sessions with a consented message-view event, split by message and offer. ‘Opened’ is the booking-funnel boundary; this is not the customer's verified age.",
    colJourney: 'Message',
    colOffer: 'Offer',
    colMeasured: 'Consented sessions',
    colOpened: 'Opened booking',
    colPlan: 'Plan intent',
    journeyControl: 'Control · 50+',
    journeyAntigym: 'Anti-gym · 25–49',
    journeyUnassigned: 'Unassigned',
    offerBaseline: '749 / 899',
    offerUnassigned: 'Unassigned',
    planTitle: 'Plan intent',
    planSub:
      'The last recorded plan choice among sessions with a consented message-view event. Sessions without a choice remain visible as Unselected.',
    planAnnual749: '12 months · 749/month',
    planFlex899: 'Flexible · 899/month',
    planAnnualUpfront8990: '12 months · 8,990 upfront',
    planUnassigned: 'Unselected',
    surfaceTitle: 'Offer placement',
    surfaceSub:
      'Consented message-view sessions per offer placement. One session may appear in several rows after seeing several placements.',
    surfaceHero: 'Hero · price slide',
    surfaceChoice: 'Two-plan choice',
    surfaceMembership: 'Membership plans',
    surfaceUnassigned: 'Unassigned',
    sessionsTitle: 'Recent sessions',
    sessionsSub: 'Newest first — expand a row for the full journey inside the modal.',
    colTime: 'Time',
    colDevice: 'Device',
    colStatus: 'Status',
    expandRow: 'Expand row',
    adDotTitle: 'Ad click',
    quizLinkTitle: 'Came from the quiz',
    emptyCalTitle: 'Saw an empty calendar',
    journeyTitle: 'Journey',
    slotsShownN: (n: number) => `${n} slots`,
    refTitle: 'Booking reference',
    refHint: 'Find the person in /bookinger with this reference.',
    deviceMobile: 'Mobile',
    deviceDesktop: 'Desktop',
    deviceUnknown: 'Unknown',
    stages: {
      opened: 'Opened',
      saw_calendar: 'Saw the calendar',
      selected_slot: 'Picked a time',
      opened_details: 'The form',
      attempted: 'Submitted',
      booked: 'Booked',
    } as Record<string, string>,
    events: {
      journey_view: 'Viewed the message journey',
      offer_view: 'Viewed the offer',
      plan_intent: 'Selected a plan',
      modal_open: 'Opened the modal',
      flow_start: 'Started the flow',
      calendar_view: 'Calendar shown',
      calendar_error: 'Calendar error',
      day_select: 'Picked a day',
      slot_select: 'Picked a time',
      details_open: 'Opened the form',
      submit_attempt: 'Submitted',
      submit_error: 'Submit error',
      booked: 'Booked ✓',
      closed: 'Closed the modal',
      intake_open: 'Opened intake',
      intake_submit: 'Answered intake',
      intake_skip: 'Skipped intake',
    } as Record<string, string>,
    errorKinds: {
      validation: 'invalid form',
      slot_taken: 'slot taken',
      server: 'server error',
      network: 'network error',
    } as Record<string, string>,
    emptyTitle: 'No booking sessions in this range',
    emptyHint:
      'Sessions show up here once the calendar beacon is live on the theme and someone opens the booking modal.',
  },
};

type Msgs = (typeof MESSAGES)['nb'];

type FunnelData = Awaited<ReturnType<typeof api.bookingFunnel>>;
type SessionRow = FunnelData['sessions'][number];
type SourceRow = FunnelData['bySource'][number];
type JourneyRow = FunnelData['byJourney'][number];
type PlanIntentRow = FunnelData['byPlanIntent'][number];
type OfferSurfaceRow = FunnelData['byOfferSurface'][number];

function useBookingFunnel(from: string, to: string) {
  return useQuery({
    queryKey: ['booking-funnel', from, to],
    queryFn: () => api.bookingFunnel({ from, to }),
  });
}

const EM_DASH = '—';
const MINUS = '−';

/** num ÷ den as a 1-decimal percent; null (→ em dash) when the denominator is 0. */
function pctOf(num: number, den: number): number | null {
  return den > 0 ? Math.round((num / den) * 1000) / 10 : null;
}

const timeFmt = new Intl.DateTimeFormat('nb-NO', { hour: '2-digit', minute: '2-digit' });
const timeSecFmt = new Intl.DateTimeFormat('nb-NO', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});
const slotFmt = new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function formatStartTime(iso: string): { time: string; date: string } {
  return { time: timeFmt.format(new Date(iso)), date: formatIsoDate(iso.slice(0, 10)).date };
}

/** Seconds → 'm:ss' (e.g. 92 → '1:32'). */
function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function formatSlot(iso: string | null): string {
  if (!iso) return EM_DASH;
  try {
    return slotFmt.format(new Date(iso));
  } catch {
    return iso;
  }
}

function sourceLabel(m: Msgs, source: SourceRow['source']): string {
  if (source === 'quiz') return m.srcQuiz;
  if (source === 'lp') return m.srcLp;
  return m.srcOther;
}

function journeyLabel(m: Msgs, journey: SessionRow['audienceJourney']): string {
  if (journey === 'control_50_plus') return m.journeyControl;
  if (journey === 'antigym_25_49') return m.journeyAntigym;
  return m.journeyUnassigned;
}

function offerLabel(m: Msgs, offer: SessionRow['offerVersion']): string {
  return offer === 'baseline-749-899' ? m.offerBaseline : m.offerUnassigned;
}

function planIntentLabel(m: Msgs, plan: SessionRow['planIntent']): string {
  if (plan === 'annual-749') return m.planAnnual749;
  if (plan === 'flex-899') return m.planFlex899;
  if (plan === 'annual-upfront-8990') return m.planAnnualUpfront8990;
  return m.planUnassigned;
}

function offerSurfaceLabel(m: Msgs, surface: OfferSurfaceRow['offerSurface']): string {
  if (surface === 'hero-price-slide') return m.surfaceHero;
  if (surface === 'two-plan-choice') return m.surfaceChoice;
  if (surface === 'membership-plans') return m.surfaceMembership;
  return m.surfaceUnassigned;
}

export function KalenderPage() {
  const m = useViewMessages(MESSAGES);
  const { from, to } = useFilters();
  const query = useBookingFunnel(from, to);

  if (query.isLoading)
    return (
      <PageFrame m={m} from={from} to={to}>
        <LoadingState />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame m={m} from={from} to={to}>
        <ErrorState onRetry={() => query.refetch()} />
      </PageFrame>
    );

  const data = query.data;
  const hasConsentedSessions = data.byJourney.some((row) => row.sessions > 0);
  const hasOperationalSessions = data.totals.sessions > 0;

  return (
    <PageFrame m={m} from={from} to={to}>
      {!hasConsentedSessions && !hasOperationalSessions ? (
        <EmptyState title={m.emptyTitle}>{m.emptyHint}</EmptyState>
      ) : (
        <>
          {hasOperationalSessions ? (
            <>
              <KpiRow m={m} totals={data.totals} />
              <HealthWarnings m={m} totals={data.totals} />
              <FunnelCard m={m} totals={data.totals} />
              {data.bySource.length > 1 ? <SourceCard m={m} rows={data.bySource} /> : null}
            </>
          ) : null}
          {hasConsentedSessions ? (
            <>
              <JourneyCard m={m} rows={data.byJourney} />
              <PlanIntentCard m={m} rows={data.byPlanIntent} />
              <OfferSurfaceCard m={m} rows={data.byOfferSurface} />
            </>
          ) : null}
          {hasOperationalSessions ? <SessionsCard m={m} rows={data.sessions} /> : null}
        </>
      )}
    </PageFrame>
  );
}

function PageFrame({
  m,
  from,
  to,
  children,
}: {
  m: Msgs;
  from: string;
  to: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="mt-1 max-w-3xl text-body text-fg-muted">
          {m.sub(formatIsoDate(from).date, formatIsoDate(to).date)}
        </p>
      </header>
      {children}
    </div>
  );
}

function CardHeading({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h2 className="text-section text-fg">{title}</h2>
      <p className="mt-0.5 max-w-3xl text-label text-fg-muted">{sub}</p>
    </div>
  );
}

function KpiRow({ m, totals }: { m: Msgs; totals: FunnelData['totals'] }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label={m.statSessions}
        source="derived"
        value={formatMetric(totals.sessions, 'int')}
        sub={m.statSessionsSub}
      />
      <StatCard
        label={m.statSlotRate}
        source="derived"
        value={formatMetric(totals.slotPct, 'pct')}
        sub={m.statSlotRateSub(formatMetric(totals.selectedSlot, 'int'))}
      />
      <StatCard
        label={m.statDetailsRate}
        source="derived"
        value={formatMetric(totals.detailsPct, 'pct')}
        sub={m.statDetailsRateSub(formatMetric(totals.openedDetails, 'int'))}
      />
      <StatCard
        label={m.statCompletionRate}
        source="booking"
        value={formatMetric(totals.completionPct, 'pct')}
        sub={m.statCompletionRateSub(formatMetric(totals.booked, 'int'))}
      />
    </div>
  );
}

/** Operational alarms — only rendered when something is actually wrong. */
function HealthWarnings({ m, totals }: { m: Msgs; totals: FunnelData['totals'] }) {
  const errorEntries = Object.entries(totals.errors);
  const warnings: string[] = [];
  if (totals.emptyCalendarSessions > 0) warnings.push(m.warnEmpty(totals.emptyCalendarSessions));
  if (totals.calendarErrorSessions > 0) warnings.push(m.warnCalErr(totals.calendarErrorSessions));
  if (errorEntries.length > 0) {
    const parts = errorEntries
      .map(([kind, n]) => `${m.errorKinds[kind] ?? kind} ×${n}`)
      .join(' · ');
    warnings.push(m.warnErrors(parts));
  }
  if (warnings.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {warnings.map((w) => (
        <div
          key={w}
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-label text-fg"
        >
          ⚠️ {w}
        </div>
      ))}
    </div>
  );
}

function FunnelCard({ m, totals }: { m: Msgs; totals: FunnelData['totals'] }) {
  const rows = [
    { key: 'opened', label: m.stageOpened, reached: totals.sessions },
    { key: 'saw', label: m.stageSawCalendar, reached: totals.sawCalendar },
    { key: 'slot', label: m.stageSelectedSlot, reached: totals.selectedSlot },
    { key: 'details', label: m.stageOpenedDetails, reached: totals.openedDetails },
    { key: 'attempted', label: m.stageAttempted, reached: totals.attempted },
    { key: 'booked', label: m.stageBooked, reached: totals.booked },
  ].map((r, i, arr) => ({
    ...r,
    pct: pctOf(r.reached, totals.sessions),
    drop: i > 0 ? Math.max(0, (arr[i - 1]?.reached ?? 0) - r.reached) : 0,
  }));

  return (
    <Card className="flex flex-col gap-3 p-3 sm:p-4">
      <CardHeading title={m.funnelTitle} sub={m.funnelSub} />
      <div className="flex flex-col gap-1.5">
        {rows.map((row) => {
          const widthPct = Math.max(3, row.pct ?? 0);
          return (
            <div key={row.key} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
              <div className="w-full text-left text-label text-fg-muted sm:w-36 sm:shrink-0 sm:text-right">
                {row.label}
              </div>
              <div className="relative h-7 flex-1 overflow-hidden rounded-md bg-surface-2">
                <div className="h-full rounded-md bg-brand/70" style={{ width: `${widthPct}%` }} />
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-label text-fg-muted tabular">
                  {formatMetric(row.reached, 'int')}
                </span>
              </div>
              <div className="flex w-full items-center gap-2 text-left text-label tabular sm:w-32 sm:shrink-0">
                <span className="text-fg-subtle">{formatMetric(row.pct, 'pct')}</span>
                {row.drop > 0 ? (
                  <Badge tone="neutral">
                    {MINUS}
                    {formatMetric(row.drop, 'int')}
                  </Badge>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function SourceCard({ m, rows }: { m: Msgs; rows: SourceRow[] }) {
  return (
    <Card className="flex flex-col gap-3 p-3 sm:p-4">
      <CardHeading title={m.sourceTitle} sub={m.sourceSub} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[32rem] text-label">
          <thead>
            <tr className="text-left text-micro text-fg-subtle">
              <th className="py-1.5 pr-3 font-medium">{m.colSource}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colSessions}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colSlot}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colAttempted}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colBooked}</th>
              <th className="py-1.5 text-right font-medium">{m.colRate}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.source} className="border-t border-border">
                <td className="py-2 pr-3 text-fg">{sourceLabel(m, r.source)}</td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(r.sessions, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(r.selectedSlot, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(r.attempted, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(r.booked, 'int')}
                </td>
                <td className="py-2 text-right tabular text-fg-subtle">
                  {formatMetric(pctOf(r.booked, r.sessions), 'pct')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function JourneyCard({ m, rows }: { m: Msgs; rows: JourneyRow[] }) {
  return (
    <Card className="flex flex-col gap-3 p-3 sm:p-4">
      <CardHeading title={m.audienceTitle} sub={m.audienceSub} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[52rem] text-label">
          <thead>
            <tr className="text-left text-micro text-fg-subtle">
              <th className="py-1.5 pr-3 font-medium">{m.colJourney}</th>
              <th className="py-1.5 pr-3 font-medium">{m.colOffer}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colMeasured}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colOpened}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colSlot}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colAttempted}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colBooked}</th>
              <th className="py-1.5 text-right font-medium">{m.colRate}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.audienceJourney ?? 'unassigned'}:${row.offerVersion ?? 'unassigned'}`}
                className="border-t border-border"
              >
                <td className="py-2 pr-3 text-fg">{journeyLabel(m, row.audienceJourney)}</td>
                <td className="py-2 pr-3 text-fg-muted">{offerLabel(m, row.offerVersion)}</td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.sessions, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.opened, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.selectedSlot, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.attempted, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.booked, 'int')}
                </td>
                <td className="py-2 text-right tabular text-fg-subtle">
                  {formatMetric(pctOf(row.booked, row.sessions), 'pct')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PlanIntentCard({ m, rows }: { m: Msgs; rows: PlanIntentRow[] }) {
  return (
    <Card className="flex flex-col gap-3 p-3 sm:p-4">
      <CardHeading title={m.planTitle} sub={m.planSub} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[52rem] text-label">
          <thead>
            <tr className="text-left text-micro text-fg-subtle">
              <th className="py-1.5 pr-3 font-medium">{m.colPlan}</th>
              <th className="py-1.5 pr-3 font-medium">{m.colOffer}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colMeasured}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colOpened}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colSlot}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colAttempted}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colBooked}</th>
              <th className="py-1.5 text-right font-medium">{m.colRate}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.planIntent ?? 'unassigned'}:${row.offerVersion ?? 'unassigned'}`}
                className="border-t border-border"
              >
                <td className="py-2 pr-3 text-fg">{planIntentLabel(m, row.planIntent)}</td>
                <td className="py-2 pr-3 text-fg-muted">{offerLabel(m, row.offerVersion)}</td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.sessions, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.opened, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.selectedSlot, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.attempted, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.booked, 'int')}
                </td>
                <td className="py-2 text-right tabular text-fg-subtle">
                  {formatMetric(pctOf(row.booked, row.sessions), 'pct')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function OfferSurfaceCard({ m, rows }: { m: Msgs; rows: OfferSurfaceRow[] }) {
  return (
    <Card className="flex flex-col gap-3 p-3 sm:p-4">
      <CardHeading title={m.surfaceTitle} sub={m.surfaceSub} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[52rem] text-label">
          <thead>
            <tr className="text-left text-micro text-fg-subtle">
              <th className="py-1.5 pr-3 font-medium">{m.surfaceTitle}</th>
              <th className="py-1.5 pr-3 font-medium">{m.colOffer}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colMeasured}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colOpened}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colSlot}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colAttempted}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colBooked}</th>
              <th className="py-1.5 text-right font-medium">{m.colRate}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.offerSurface ?? 'unassigned'}:${row.offerVersion ?? 'unassigned'}`}
                className="border-t border-border"
              >
                <td className="py-2 pr-3 text-fg">{offerSurfaceLabel(m, row.offerSurface)}</td>
                <td className="py-2 pr-3 text-fg-muted">{offerLabel(m, row.offerVersion)}</td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.sessions, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.opened, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.selectedSlot, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.attempted, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(row.booked, 'int')}
                </td>
                <td className="py-2 text-right tabular text-fg-subtle">
                  {formatMetric(pctOf(row.booked, row.sessions), 'pct')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** One status badge per session: Booket ▸ Sendte inn ▸ … ▸ Åpnet. */
function statusBadge(
  m: Msgs,
  row: SessionRow,
): { label: string; tone: 'good' | 'booking' | 'neutral' | 'bad' } {
  if (row.stage === 'booked') return { label: m.stages.booked ?? row.stage, tone: 'good' };
  if (row.errorKinds.length > 0) {
    const lastKind = row.errorKinds[row.errorKinds.length - 1] ?? '';
    return {
      label: `${m.stages[row.stage] ?? row.stage} · ${m.errorKinds[lastKind] ?? lastKind}`,
      tone: 'bad',
    };
  }
  if (row.stage === 'attempted' || row.stage === 'opened_details' || row.stage === 'selected_slot')
    return { label: m.stages[row.stage] ?? row.stage, tone: 'booking' };
  return { label: m.stages[row.stage] ?? row.stage, tone: 'neutral' };
}

function SessionsCard({ m, rows }: { m: Msgs; rows: SessionRow[] }) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card className="flex flex-col gap-3 p-3 sm:p-4">
      <CardHeading title={m.sessionsTitle} sub={m.sessionsSub} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[52rem] text-label">
          <thead>
            <tr className="text-left text-micro text-fg-subtle">
              <th className="w-8 py-1.5" aria-hidden />
              <th className="py-1.5 pr-3 font-medium">{m.colTime}</th>
              <th className="py-1.5 pr-3 font-medium">{m.colDevice}</th>
              <th className="py-1.5 pr-3 font-medium">{m.colSource}</th>
              <th className="py-1.5 pr-3 font-medium">{m.colJourney}</th>
              <th className="py-1.5 pr-3 font-medium">{m.colPlan}</th>
              <th className="py-1.5 font-medium">{m.colStatus}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isOpen = open === row.sessionId;
              const started = formatStartTime(row.startedAt);
              const badge = statusBadge(m, row);
              const device =
                row.device === 'mobile'
                  ? m.deviceMobile
                  : row.device === 'desktop'
                    ? m.deviceDesktop
                    : m.deviceUnknown;
              return (
                <Fragment key={row.sessionId}>
                  <tr
                    className="cursor-pointer border-t border-border hover:bg-surface-2/60"
                    onClick={() => setOpen(isOpen ? null : row.sessionId)}
                  >
                    <td className="py-2 pr-1">
                      <ChevronRight
                        aria-label={m.expandRow}
                        className={cn(
                          'size-4 text-fg-subtle transition-transform',
                          isOpen && 'rotate-90',
                        )}
                      />
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 tabular text-fg">
                      {started.time}
                      <span className="ml-1.5 text-micro text-fg-subtle">{started.date}</span>
                      <span className="ml-1.5 text-micro text-fg-subtle">
                        ({formatDuration(row.durationSec)})
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-fg-muted">{device}</td>
                    <td className="py-2 pr-3 text-fg-muted">
                      {sourceLabel(m, row.source)}
                      {row.hasFbclid ? (
                        <span
                          className="ml-1.5 inline-block size-1.5 rounded-full bg-accent-click align-middle"
                          title={m.adDotTitle}
                        />
                      ) : null}
                      {row.quizSessionId ? (
                        <span className="ml-1.5 text-micro text-fg-subtle" title={m.quizLinkTitle}>
                          ⇠quiz
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-fg-muted">
                      {journeyLabel(m, row.audienceJourney)}
                    </td>
                    <td className="py-2 pr-3 text-fg-muted">
                      {planIntentLabel(m, row.planIntent)}
                    </td>
                    <td className="py-2">
                      <Badge tone={badge.tone}>{badge.label}</Badge>
                      {row.sawEmptyCalendar ? (
                        <span className="ml-1.5 text-micro text-fg-subtle" title={m.emptyCalTitle}>
                          ∅
                        </span>
                      ) : null}
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr className="border-t border-border bg-surface-2/40">
                      <td colSpan={7} className="p-3">
                        <SessionDetail m={m} row={row} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SessionDetail({ m, row }: { m: Msgs; row: SessionRow }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-label text-fg-muted">
        {m.colJourney}: <span className="text-fg">{journeyLabel(m, row.audienceJourney)}</span>
        <span className="text-fg-subtle">
          {' '}
          · {m.colOffer}: {offerLabel(m, row.offerVersion)} · {m.colPlan}:{' '}
          {planIntentLabel(m, row.planIntent)} · {m.surfaceTitle}:{' '}
          {row.offerSurfaces.length
            ? row.offerSurfaces.map((surface) => offerSurfaceLabel(m, surface)).join(', ')
            : m.surfaceUnassigned}
        </span>
      </p>
      {row.ad.adName || row.ad.campaign ? (
        <p className="text-label text-fg-muted">
          {m.adDotTitle}: <span className="text-fg">{row.ad.adName ?? EM_DASH}</span>
          {row.ad.campaign ? <span className="text-fg-subtle"> · {row.ad.campaign}</span> : null}
        </p>
      ) : null}
      {row.slotIso ? (
        <p className="text-label text-fg-muted">
          {m.stageSelectedSlot}: <span className="text-fg">{formatSlot(row.slotIso)}</span>
        </p>
      ) : null}
      {row.refCode ? (
        <p className="text-label text-fg-muted">
          {m.refTitle}: <span className="font-mono text-fg">{row.refCode}</span>
          <span className="ml-2 text-micro text-fg-subtle">{m.refHint}</span>
        </p>
      ) : null}
      <div>
        <p className="mb-1 text-micro font-semibold uppercase tracking-wide text-fg-subtle">
          {m.journeyTitle}
        </p>
        <ol className="flex flex-col gap-0.5">
          {row.journey.map((j) => (
            <li
              key={`${row.sessionId}:${j.at}:${j.event}:${j.stepName ?? ''}:${j.slotIso ?? ''}:${j.dayIso ?? ''}:${j.errorKind ?? ''}:${j.planIntent ?? ''}:${j.offerSurface ?? ''}`}
              className="flex items-baseline gap-2 text-label text-fg-muted"
            >
              <span className="w-20 shrink-0 tabular text-fg-subtle">
                {timeSecFmt.format(new Date(j.at))}
              </span>
              <span>
                {m.events[j.event] ?? j.event}
                {j.event === 'calendar_view' && j.slotsShown !== null ? (
                  <span className="text-fg-subtle"> · {m.slotsShownN(j.slotsShown)}</span>
                ) : null}
                {j.slotIso && j.event !== 'calendar_view' ? (
                  <span className="text-fg-subtle"> · {formatSlot(j.slotIso)}</span>
                ) : null}
                {j.errorKind ? (
                  <span className="text-fg-subtle">
                    {' '}
                    · {m.errorKinds[j.errorKind] ?? j.errorKind}
                  </span>
                ) : null}
                {j.event === 'plan_intent' && j.planIntent ? (
                  <span className="text-fg-subtle"> · {planIntentLabel(m, j.planIntent)}</span>
                ) : null}
                {j.offerSurface ? (
                  <span className="text-fg-subtle"> · {offerSurfaceLabel(m, j.offerSurface)}</span>
                ) : null}
                {j.event === 'closed' && j.stepName ? (
                  <span className="text-fg-subtle"> · {j.stepName}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
